import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Cpu, Brain, Sparkles, Shield, Boxes, GitMerge,
  Server, Monitor, Cloud, MessageSquare, Video,
  GraduationCap, HeartHandshake, FileText, ArrowRightLeft,
  ChevronDown, ArrowRight, Share2, Repeat, UserCog, Puzzle, Headphones, BadgeCheck,
} from 'lucide-react';

interface MenuItem {
  icon: React.ElementType;
  label: string;
  description: string;
  href: string;
  badge?: string;
}

interface Section {
  title: string;
  items: MenuItem[];
}

interface Featured {
  icon: React.ElementType;
  eyebrow: string;
  title: string;
  description: string;
  cta: string;
  href: string;
}

interface Menu {
  id: string;
  label: string;
  badge?: string;
  featured?: Featured;
  sections: Section[];
  footer?: { label: string; href: string };
}

const ZimletMegaMenu = () => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const item = (icon: React.ElementType, key: string, href: string, badge?: string): MenuItem => ({
    icon,
    label: t(`aiMenu.${key}.label`),
    description: t(`aiMenu.${key}.description`),
    href,
    badge,
  });

  const menuItems: Menu[] = [
    {
      id: 'ai-products',
      label: t('aiMenu.products'),
      badge: 'AI',
      sections: [
        {
          title: t('aiMenu.aiSolutionsTitle'),
          items: [
            item(Brain, 'zimbraSovereignAI', '/products/zimbra-sovereign-ai', 'Flagship'),
            item(Sparkles, 'aiInfrastructure', '/products/ai-infrastructure', 'NEW'),
            item(Boxes, 'aiOpenSourceStack', '/products/ai-open-source-stack', 'NEW'),
          ],
        },
        {
          title: t('aiMenu.platformTitle'),
          items: [
            item(Video, 'nextSolution', '/products/next'),
            item(Monitor, 'scalenixos', '/scalenix-os'),
            item(Server, 'zimbraAdminPanel', '/zimbra-admin-panel'),
          ],
        },
      ],
      footer: { label: t('aiMenu.viewAllProducts'), href: '/products' },
    },
    {
      id: 'ai-training',
      label: t('aiMenu.training'),
      badge: 'AI',
      sections: [
        {
          title: t('aiMenu.aiTrainingTracksTitle'),
          items: [
            item(GraduationCap, 'scalenixUniversity', '/university', 'NEW'),
            item(Cpu, 'aiInfraTraining', '/training#ai-infra'),
            item(Brain, 'aiBspAdminTraining', '/training#ai-zimbra-bsp'),
            item(Sparkles, 'aiServicesTraining', '/training#ai-services'),
            item(Shield, 'aiIdentityTraining', '/training#ai-identity'),
          ],
        },
        {
          title: t('aiMenu.advancedTracksTitle'),
          items: [
            item(Boxes, 'aiOpenSourceTraining', '/training#ai-open-source'),
            item(GitMerge, 'aiDevopsTraining', '/training#ai-devops'),
          ],
        },
      ],
      footer: { label: t('aiMenu.requestTraining'), href: '/training#training-request' },
    },
    {
      id: 'products',
      label: t('aiMenu.classicProducts'),
      featured: {
        icon: Video,
        eyebrow: t('aiMenu.featuredProduct.eyebrow'),
        title: t('aiMenu.featuredProduct.title'),
        description: t('aiMenu.featuredProduct.description'),
        cta: t('aiMenu.featuredProduct.cta'),
        href: '/products/next',
      },
      sections: [
        {
          title: t('aiMenu.collabTitle'),
          items: [
            item(MessageSquare, 'zimletNext', '/products/zimlet-next'),
            item(Share2, 'zimbraShareGateway', '/products/zimbra-share-gateway', 'NEW'),
            item(BadgeCheck, 'fcshare', '/products/fcshare', 'NEW'),
            item(Cloud, 'zimletNextcloud', '/products/zimlet-nextcloud'),
          ],
        },
        {
          title: t('aiMenu.migrationIdentityTitle'),
          items: [
            item(Repeat, 'exchangeToZimbra', '/products/exchange-to-zimbra', 'NEW'),
            item(UserCog, 'ldapProvisioning', '/products/ldap-zimbra-provisioning', 'NEW'),
            item(ArrowRightLeft, 'zimbraMigration', '/products/zimbra-migration'),
          ],
        },
      ],
      footer: { label: t('aiMenu.viewAllProducts'), href: '/products' },
    },
    {
      id: 'services',
      label: t('aiMenu.services'),
      featured: {
        icon: Headphones,
        eyebrow: t('aiMenu.featuredService.eyebrow'),
        title: t('aiMenu.featuredService.title'),
        description: t('aiMenu.featuredService.description'),
        cta: t('aiMenu.featuredService.cta'),
        href: '/contact',
      },
      sections: [
        {
          title: t('aiMenu.servicesConsultingTitle'),
          items: [
            item(FileText, 'aiConsulting', '/services#ai-consulting'),
            item(Cloud, 'managedCloud', '/services#managed-cloud'),
            item(Repeat, 'migrationService', '/services#migration'),
            item(BadgeCheck, 'fcshareService', '/services#fcshare', 'NEW'),
          ],
        },
        {
          title: t('aiMenu.servicesSupportTitle'),
          items: [
            item(GraduationCap, 'trainingService', '/services#training'),
            item(Puzzle, 'integrationService', '/services#integration'),
            item(HeartHandshake, 'support', '/services#support'),
          ],
        },
      ],
      footer: { label: t('aiMenu.viewAllServices'), href: '/services' },
    },
  ];

  const open = (menuId: string) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setActiveMenu(menuId);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActiveMenu(null), 120);
  };

  const go = (href: string) => {
    setActiveMenu(null);
    navigate(href);
  };

  const renderItem = (entry: MenuItem) => {
    const Icon = entry.icon;
    return (
      <button
        key={entry.label}
        onClick={() => go(entry.href)}
        className="group relative w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all duration-200 hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
      >
        <div className="relative flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-400/10 border border-white/10 flex items-center justify-center transition-all duration-300 group-hover:from-blue-500 group-hover:to-cyan-400 group-hover:border-transparent group-hover:shadow-[0_0_20px_rgba(34,211,238,0.45)]">
          <Icon className="h-[18px] w-[18px] text-cyan-300 transition-colors group-hover:text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white flex items-center gap-2">
            <span className="truncate">{entry.label}</span>
            {entry.badge && (
              <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r from-blue-500 to-cyan-400 text-slate-950 px-2 py-0.5 rounded-full shrink-0">
                {entry.badge}
              </span>
            )}
          </p>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed line-clamp-2 group-hover:text-slate-300 transition-colors">
            {entry.description}
          </p>
        </div>
        <ArrowRight className="h-4 w-4 mt-3 text-cyan-300 opacity-0 -translate-x-2 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
      </button>
    );
  };

  const renderFeatured = (featured: Featured) => {
    const Icon = featured.icon;
    return (
      <button
        onClick={() => go(featured.href)}
        className="group relative overflow-hidden rounded-2xl p-6 text-left flex flex-col justify-between min-h-[280px] bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.25) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-cyan-300/40 blur-3xl transition-transform duration-700 group-hover:scale-125" />
        <div className="relative">
          <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur border border-white/25 flex items-center justify-center mb-6">
            <Icon className="h-6 w-6 text-white" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-100">{featured.eyebrow}</p>
          <p className="mt-2 text-xl font-bold text-white leading-tight">{featured.title}</p>
          <p className="mt-3 text-sm text-blue-50/90 leading-relaxed">{featured.description}</p>
        </div>
        <span className="relative mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white">
          {featured.cta}
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </button>
    );
  };

  return (
    <nav ref={menuRef} className="border-t border-white/5" aria-label="Main">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-14 gap-2">
          {menuItems.map((menu) => {
            const isOpen = activeMenu === menu.id;
            return (
              <div
                key={menu.id}
                onMouseEnter={() => open(menu.id)}
                onMouseLeave={scheduleClose}
              >
                <button
                  onClick={() => (isOpen ? setActiveMenu(null) : open(menu.id))}
                  aria-expanded={isOpen}
                  className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-all duration-200 ${
                    isOpen
                      ? 'text-white bg-white/10 ring-1 ring-cyan-400/40 shadow-[0_0_24px_-6px_rgba(34,211,238,0.6)]'
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {menu.label}
                  {menu.badge && (
                    <span className="inline-flex items-center text-[10px] font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 text-slate-950 px-2 py-0.5 rounded-full shrink-0">
                      {menu.badge}
                    </span>
                  )}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <div
                    className="absolute left-4 right-4 sm:left-6 sm:right-6 lg:left-8 lg:right-8 top-full pt-3 z-50"
                    onMouseEnter={() => open(menu.id)}
                    onMouseLeave={scheduleClose}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                      className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-950/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(2,6,23,0.7)]"
                    >
                      <div className="pointer-events-none absolute -top-32 left-1/3 w-96 h-64 rounded-full bg-blue-600/25 blur-3xl" />
                      <div className="pointer-events-none absolute -bottom-32 right-0 w-80 h-64 rounded-full bg-cyan-500/15 blur-3xl" />
                      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />

                      <div className={`relative p-6 grid gap-6 ${menu.featured ? 'grid-cols-[280px_1fr_1fr]' : 'grid-cols-2'}`}>
                        {menu.featured && renderFeatured(menu.featured)}
                        {menu.sections.map((section) => (
                          <div key={section.title}>
                            <h3 className="px-3 text-[11px] font-semibold text-cyan-300/80 uppercase tracking-[0.18em] mb-3">
                              {section.title}
                            </h3>
                            <div className="space-y-1">{section.items.map(renderItem)}</div>
                          </div>
                        ))}
                      </div>

                      {menu.footer && (
                        <div className="relative border-t border-white/10 px-6 py-4 flex justify-end bg-white/[0.02]">
                          <button
                            onClick={() => go(menu.footer!.href)}
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-white transition-colors"
                          >
                            {menu.footer.label}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </button>
                        </div>
                      )}
                    </motion.div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default ZimletMegaMenu;
